import { Button } from '@avanta/shared/components/ui/Button';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useDialogContext } from '@avanta/shared/contexts/Dialog';
import { isPastDate as isPastDateUtils } from '@avanta/shared/utils/date';
import { Calendar, ChevronRight, Clock, Timer } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTagMap } from 'hooks/useWellbeingContent';
import { useComponentTracking } from 'tracking/hooks/useTrackingComponent';
import { useTrackingEvents } from 'tracking/hooks/useTrackingEvents';
import { ResourceType } from 'types/tracking';
import { type Wellbeing } from 'types/wellbeing';
import { openInNewTab } from 'utils/common';
import { formatUnifiedDateSimple, formatUnifiedTime } from 'utils/date';
import { resolveTrackingCategory } from 'utils/tracking';
import { getMonthFromDate, getTagCategoryIcon, tagColorVar } from 'utils/wellbeing';
import { VideoDialog } from '../../../VideoDialog';
import { trainingCardStyles } from './styles';

const {
  shell,
  mobileTopRule,
  mainRow,
  desktopImageAside,
  coverImage,
  bodyColumn,
  tagsRow,
  tagsRowSpacer,
  monthBadge,
  categoryLine,
  glyph,
  metaGlyph,
  title: titleClass,
  thumbnailAndMetaRow,
  mobileThumbnailWrap,
  metaRow,
  metaItem,
  cta,
  ctaChevron,
} = trainingCardStyles;

interface TrainingCardProps {
  training: Wellbeing;
}

export const TrainingCard = ({ training }: TrainingCardProps): React.ReactElement => {
  const { showDialog } = useDialogContext();
  const { trackResourceOpen } = useComponentTracking();
  const { trackTrainingSessionAttended } = useTrackingEvents();
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);

  const { title, imageURL, tags, startTime, durationInMinutes, trainingLink, videoURL } = training;

  const isPastDate = !!(startTime && isPastDateUtils(startTime));
  const monthTag = startTime ? getMonthFromDate(startTime) : null;
  const tagMap = useTagMap();
  const primaryTag = tags?.[0];
  const displayTag = tagMap?.get(primaryTag ?? '');
  const tagName = displayTag?.name ?? primaryTag;
  const TagIcon = getTagCategoryIcon(displayTag?.id ?? primaryTag ?? '');
  const tagColor = tagColorVar(displayTag?.colorText);
  const durationLabel = durationInMinutes ? `${durationInMinutes}min` : '';
  const buttonText = videoURL
    ? t('TrainingCard.watchRecording.cta')
    : t('ContentCard.inscription.cta');

  const handleClick = async (): Promise<void> => {
    const category = resolveTrackingCategory(displayTag ?? primaryTag);

    if (videoURL) {
      showDialog({
        component: <VideoDialog video={videoURL} title={title} isVertical={training.isVertical} />,
      });
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.TIPS,
        category,
      });
      return;
    }

    if (trainingLink) {
      await trackResourceOpen({
        resourceId: title,
        resourceType: ResourceType.TRAINING,
        category,
      });
      if (startTime && !isPastDate) {
        trackTrainingSessionAttended({
          name: title,
          date: startTime,
          category,
        });
      }
      openInNewTab(trainingLink);
      return;
    }
  };

  return (
    <div className={shell}>
      <div className={mobileTopRule} aria-hidden />
      <div className={mainRow}>
        <div className={desktopImageAside}>
          {imageURL && <img src={imageURL} alt={title} className={coverImage} />}
        </div>
        <div className={bodyColumn}>
          <div className={tagsRow}>
            {monthTag && <span className={monthBadge}>{monthTag}</span>}
            {(monthTag ?? tagName) && <span className={tagsRowSpacer} aria-hidden />}
            {tagName && (
              <div className={categoryLine} style={{ color: tagColor }}>
                <TagIcon className={glyph} aria-hidden />
                {tagName}
              </div>
            )}
          </div>
          <h4 className={titleClass}>{title}</h4>
          <div className={thumbnailAndMetaRow}>
            <div className={mobileThumbnailWrap}>
              {imageURL && <img src={imageURL} alt={title} className={coverImage} />}
            </div>
            <div className={metaRow}>
              {startTime && (
                <>
                  <span className={metaItem}>
                    <Calendar className={metaGlyph} aria-hidden />
                    {formatUnifiedDateSimple(startTime)}
                  </span>
                  <span className={metaItem}>
                    <Clock className={metaGlyph} aria-hidden />
                    {formatUnifiedTime(startTime)}
                  </span>
                </>
              )}
              {durationLabel && (
                <span className={metaItem}>
                  <Timer className={metaGlyph} aria-hidden />
                  {durationLabel}
                </span>
              )}
            </div>
          </div>
          <Button
            variant="terciary"
            size="lg"
            className={cta}
            onClick={handleClick}
            icon={!videoURL ? 'trailing' : undefined}
          >
            {buttonText}
            {!videoURL && <ChevronRight className={ctaChevron} aria-hidden />}
          </Button>
        </div>
      </div>
    </div>
  );
};
